import React, { FunctionComponent } from 'react';
import { useAtom } from 'jotai';
import { useTheme } from '@mui/material/styles';
import { useTranslation } from 'react-i18next'

import { currentPracticeAtom } from '../../../../lib/practice';

import {
  Box,
  ButtonGroup,
  Grid,
  Paper,
} from '@mui/material';
import Button from '@mui/material/Button';
import {
  Switcher,
  Typography
} from '../../../../components/v2/styled';

import HelpersClientWidgets from '../../../../actions/helpers/client_widgets';

// Status widget settings. Object onder één value key (`status`):
//   { amount: number, items: Record<string,boolean> }
// - `amount`: hoeveel knopen de tijdlijn toont — 10 (default), 20 of 30.
// - `items` : per source aan/uit. NIET sorteerbaar: de runtime sorteert
//             chronologisch, een volgorde-voorkeur bestaat niet.
// Consent-/module-gated sources krijgen een disabled switch (kan niet aan
// zolang de practice het niet toelaat). Er is bewust géén cta-optie: er
// bestaat geen aparte status-pagina om naartoe te navigeren.

export type StatusSettingsValue = {
  amount?: number;
  items?: Record<string, boolean>;
};

type Props = {
  value: StatusSettingsValue;
  onChange: (v: StatusSettingsValue) => void;
  // Unused voor deze widget (geen order). Blijft in het gedeelde
  // SettingsProps-shape zodat de dialoog uniform rendert.
  order?: string[];
  onOrderChange?: (v: string[]) => void;
};

const LABEL_BASE = 'coach.personal_settings.userdetail_widgets.settings.status';

export const CoachSettingsWidgetStatus: FunctionComponent<Props> = ({
  value,
  onChange
}) => {
  const theme = useTheme();
  const {t} = useTranslation(['translations']);
  const [currentPractice, setCurrentPractice] = useAtom(currentPracticeAtom);

  const amount = HelpersClientWidgets.getStatusAmount(value);
  const items = value?.items || {};

  const orderedItems = HelpersClientWidgets.getOrderedStatusItems(items, currentPractice, null);

  const setItems = (nextItems: Record<string, boolean>) => onChange({ ...value, items: nextItems });

  return <Grid container spacing={2}>
    <Grid item xs={12}>
      <Typography variant="caption" sx={{color: 'text.secondary'}}>
        {t(`${LABEL_BASE}.description`)}
      </Typography>
    </Grid>

    {/* Per-source schakelaars, vaste volgorde (chronologisch sorteren is een
        runtime-aangelegenheid). Disabled = module/consent staat het niet toe. */}
    <Grid item xs={12}>
      <Typography variant="subtitle2" sx={{color: 'text.secondary'}}>
        {t(`${LABEL_BASE}.items.title`)}
      </Typography>
      <Paper variant="outlined" sx={{padding: 1}}>
        {orderedItems.map((item) => {
          const { Icon } = item;
          const stateColor = item.state === 'active'
            ? theme.palette.primary.main
            : item.state === 'inactive'
              ? theme.palette.secondary.main
              : theme.palette.text.disabled;
          return <Box key={item.key} sx={{display: 'flex', alignItems: 'center', gap: 1, padding: 1, borderRadius: 0.5, marginTop: 0.5}}>
            <Icon sx={{fontSize: '1.2rem', color: stateColor}} />
            <Switcher
              label={t(`${LABEL_BASE}.items.${item.key}`)}
              value={item.state === 'active'}
              disabled={item.state === 'disabled'}
              greying
              onChange={(c) => setItems({ ...items, [item.key]: c })}
            />
          </Box>;
        })}
      </Paper>
    </Grid>

    {/* Widget-level opties: de goto-CTA + het aantal knopen. */}
    <Grid item xs={12}>
      <Typography variant="subtitle2" sx={{color: 'text.secondary'}}>
        {t(`${LABEL_BASE}.options.title`)}
      </Typography>
      <Paper variant="outlined" sx={{padding: 1, marginTop: 0.5}}>
        <Box sx={{display: 'flex', flexWrap: 'wrap', alignItems: 'center', columnGap: 1, rowGap: 0.5, padding: 1, borderRadius: 0.5, marginTop: 0.5}}>
          <Typography variant="body2" sx={{color: 'text.secondary', width: '100%'}}>
            {t(`${LABEL_BASE}.options.amount`)}
          </Typography>
          <ButtonGroup>
            {HelpersClientWidgets.statusAmounts.map((a) => {
              const selected = amount === a.key;
              return <Button
                key={a.key}
                variant={selected ? "contained" : "outlined"}
                onClick={() => onChange({ ...value, amount: a.key })}
              >
                {a.key}
              </Button>;
            })}
          </ButtonGroup>
        </Box>
      </Paper>
    </Grid>
  </Grid>;
}

// Read-only status: de source-iconen (vaste volgorde) gekleurd per staat,
// + het amount op een tweede rij.
type StatusProps = {
  value: StatusSettingsValue;
  order?: string[];
};

export const CoachSettingsWidgetStatusStatus: FunctionComponent<StatusProps> = ({
  value
}) => {
  const theme = useTheme();
  const {t} = useTranslation(['translations']);
  const [currentPractice, setCurrentPractice] = useAtom(currentPracticeAtom);

  const items = value?.items || {};
  const amount = HelpersClientWidgets.getStatusAmount(value);

  const orderedItems = HelpersClientWidgets.getOrderedStatusItems(items, currentPractice, null);

  const colorFor = (state: 'active' | 'inactive' | 'disabled' | string): string => {
    switch (state) {
      case 'active':   return theme.palette.primary.main;
      case 'inactive': return theme.palette.secondary.main;
      default:         return theme.palette.text.disabled;
    }
  };

  return <Box sx={{display: 'flex', flexDirection: 'column', gap: 0.5, marginTop: 1}}>
    <Box sx={{
      display: 'flex',
      flexWrap: 'wrap',
      justifyContent: 'center',
      alignItems: 'center',
      gap: 1
    }}>
      {orderedItems.map((item) => {
        const { Icon } = item;
        return <Box
          key={item.key}
          title={t(`${LABEL_BASE}.items.${item.key}`)}
        >
          <Icon sx={{ fontSize: '1.1rem', color: colorFor(item.state) }} />
        </Box>;
      })}
    </Box>
    <Box sx={{display: 'flex', justifyContent: 'center', alignItems: 'center', gap: 1.5}}>
      <Box title={`${t(`${LABEL_BASE}.options.amount`)}: ${amount}`}>
        <Typography variant="caption" sx={{color: theme.palette.primary.main}}>{amount}</Typography>
      </Box>
    </Box>
  </Box>;
}
